import { Box, Text } from "../../../mads";

import { colors } from "@indeed/ifl-tokens";
import React, { useMemo } from "react";
import { StyleSheet, type ViewStyle } from "react-native";

type LabelSize = "sm" | "md";

const styles = StyleSheet.create({
  box: {
    flexDirection: "row",
  },
  disabled: {
    opacity: 0.4,
    pointerEvents: "none",
    userSelect: "none",
  },
});

export interface LabelProps {
  /**
   * Styles the label as disabled.
   */
  disabled?: boolean;
  /**
   * Styles the label as invalid.
   */
  invalid?: boolean;
  /**
   * Styles the label as required by displaying an asterisk.
   */
  showRequiredIndicator?: boolean;
  /**
   * Size of the label. Use the values `sm` or `md`. Defaults to 'md'
   */
  size?: LabelSize;

  /**
   * the string contents that should populate the label
   */
  children: string;

  /**
   * the fontweight to use for the label, defaults to bold
   */
  fontWeight?: "bold" | "normal";

  /**
   * override the color of the text, will be overriden by the disabled property
   */
  color?: string;
}

/**
 * @see https://experience.indeed.design/components/ids/label
 */
export function Label({
  children,
  showRequiredIndicator = false,
  size = "md",
  invalid = false,
  disabled = false,
  fontWeight = "bold",
  color,
}: LabelProps) {
  const textLevel = size === "sm" ? 2 : 3;
  const textColor = invalid
    ? colors.foreground.status.critical["[base]"]
    : (color ?? colors.foreground.strong);
  const boxStyle: ViewStyle = useMemo(
    () => ({
      ...styles.box,
      ...(disabled ? styles.disabled : {}),
    }),
    [disabled],
  );

  return (
    <Box sx={boxStyle}>
      <Text level={textLevel} sx={{ color: textColor, fontWeight }}>
        {children}
      </Text>
      {showRequiredIndicator && (
        <Box aria-hidden={true}>
          <Text
            sx={{
              color: colors.foreground.status.critical["[base]"],
              fontWeight: "bold",
            }}
          >
            {"\u00A0*"}
          </Text>
        </Box>
      )}
    </Box>
  );
}
